import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import {
  ContentLoading,
  Logging,
  Notification,
  NotificationData,
  settingCustomerCustomField,
  useWindowSize,
} from '@vcd-ecm-ui/Common';
import {
  VtxButton,
  VtxColumnProps,
  VtxIcon,
  VtxPaginationConfig,
  VtxTable,
  VtxTooltip,
} from '@vertexinc/vtx-ui-react-component-library';
import GenericGridProps, { ShowTableColumnsEnum } from './GenericGridProps.interface';
import {
  GridFilter,
  GridFiltersSortersPaging,
  GridSort,
  ReducerContext,
} from './Interfaces';
import { initialReportState, reportReducer, ReportState } from './State/reportReducer';
import {
  clearNotification,
  getGridDataServerModel,
  getGridDataClientModel,
  setNotification,
  getGridDataStaticModel,
  setFiltersSorters,
  setPaginationFiltersSorters,
  deleteFilter,
  addFilter,
  setFiltersSortersReady,
} from './State/reportActions';
import TableManagement from '../TableManagement/TableManagement';
import './generic-grid.scss';
import {
  ShowBreadCrumbsEnum,
  TableColumn,
  TableSettings,
} from '../..';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import { SaveFiltersSorterEnum } from '../TableManagement/TableSettings';
import BulkActions from '../BulkActions/BulkActions';

declare type SortOrder = 'descend' | 'ascend' | null;
declare type Key = React.Key;

interface SorterResult {
  order?: SortOrder;
  field?: Key | Key[];
  columnKey?: Key;
}

// eslint-disable-next-line @typescript-eslint/ban-types
const GenericGrid = <T extends Object>({
  id,
  dataTypeDescription,
  showLoadingMessage = true,
  model,
  expandedRowRender,
  rowExpandable,
  getDefaultColumns,
  rowParser,
  apiGetRowsServerModel,
  apiGetRowsClientModel,
  verticalHeightOffset = undefined,
  initialPageSize = 25,
  virtualPageSize = 200,
  pageSizeOptions = ['25', '50', '100'],
  useCache = true,
  hidePagingOnSinglePage = true,
  getRowKey,
  selectionType = 'multiple',
  expanderIcons,
  expandRowByClick = false,
  dataRows = null,
  autoRefreshSeconds = null,
  horizontalWidth = 'max-content',
  showTableManagement = false,
  showAllFavoriteColumns = false,
  showClearFilters = false,
  showClearSorters = false,
  additionalTableSettings = null,
  onFilterSortingChange = null,
  externalFilter = null,
  refreshKey = null,
  alwaysHiddenFilterBreadcrumbs = [],
  alwaysHiddenSorterBreadcrumbs = [],
  bulkActions = null,
  showLoadingIndicator = true,
  onSelectedRowsChange = null,
}: GenericGridProps<T>): JSX.Element => {
  // #region State

  const [state, reportDispatch] = React.useReducer(reportReducer, {
    ...initialReportState,
    rowParser,
    pageSize: initialPageSize,
    virtualPageSize,
    useCache: model === 'server' ? useCache : true, // Cache always used for Client & Static Side Model
    model,
    dataTypeDescription,
  });

  // Determine if Header Row must be shown above Table
  const showHeader = bulkActions || showTableManagement || showAllFavoriteColumns || showClearFilters || showClearSorters;

  const [showTableManagementDialog, setShowTableManagementDialog] = React.useState(false);
  const [columns, setColumns] = React.useState(null);
  const [tableSettingsHash, setTableSettingsHash] = React.useState('INITIAL');
  const [showTableColumns, setShowTableColumns] = React.useState(ShowTableColumnsEnum.FAVORITE);

  // Selected Rows & Row Keys
  const [selectedRowKeys, setSelectedRowKeys] = React.useState(null);
  const [selectedRows, setSelectedRows] = React.useState([]);

  // Keys for Session and Local Storage
  const tableKey = `${id}-TableSettings`;
  const filtersSortersPagingKey = `${tableKey}-FiltersSortersPaging`;

  const dispatch = useDispatch();

  // Context for all Reducer Actions
  const reducerContext: ReducerContext = {
    state,
    dispatch: reportDispatch,
    apiGetRowsServerModel,
    apiGetRowsClientModel,
  };

  // #endregion

  // #region Default & User Table Setting

  // Generate the Default Table Settings from the Columns and Additional Table Settings
  const getDefaultTableSettings = (): TableSettings => {
    const tableSettings: TableSettings = {
      version: additionalTableSettings ? additionalTableSettings.version : 'v1',
      tableKey,
      saveFiltersSorter: additionalTableSettings ? additionalTableSettings.saveFiltersSorter : SaveFiltersSorterEnum.NEVER,
      showBreadcrumbs: additionalTableSettings ? additionalTableSettings.showBreadcrumbs : ShowBreadCrumbsEnum.NEVER,
      hideDisabledColumns: additionalTableSettings ? additionalTableSettings.hideDisabledColumns : false,
      columns: [],
    };

    const defaultColumns: VtxColumnProps<T>[] = getDefaultColumns();

    // Loop through the Default Columns
    defaultColumns.forEach((column: VtxColumnProps<T>) => {
      const tableColumn: TableColumn = {
        name: column.title.toString(),
        dataIndex: column.dataIndex.toString(),
        visibility: true,
        canChangeVisibilty: true,
        pin: column.fixed?.toString(),
        canChangePin: true,
        canMove: true,
      };

      // Check if the Column was configured in the Additional Settings
      const additionalTableColumn: TableColumn = additionalTableSettings
        ? additionalTableSettings.columns.filter((x: TableColumn) => x.dataIndex === column.dataIndex)[0]
        : null;

      // Merge in the Additional Table Settings if Supplied
      if (additionalTableColumn) {
        const result: TableColumn = { ...tableColumn, ...additionalTableColumn };
        tableSettings.columns.push(result);
      } else {
        tableSettings.columns.push(tableColumn);
      }
    });

    return tableSettings;
  };

  // Verify the Table Settings.  Update if Columns were Added or Removed
  const getVerifiedTableSettings = (defaultColumns: VtxColumnProps<T>[]): TableSettings => {
    // Get the Table Settimgs from the Local Storage
    let tableSettings: TableSettings = localStorage[tableKey] ? JSON.parse(localStorage[tableKey]) : null;

    // Table Settings is defined
    if (tableSettings) {
      let columnsChanged = false;

      const left: TableColumn[] = [];
      const center: TableColumn[] = [];
      const right: TableColumn[] = [];

      // Loop through the Table Settings from Local Storage.  Check that all the Columns are still valid
      tableSettings.columns.forEach((column: TableColumn) => {
        // Find the Column in the Default Columns
        const defaultColumn: VtxColumnProps<T> = defaultColumns.filter((x: VtxColumnProps<T>) => x.dataIndex === column.dataIndex)[0];

        // Column still exists
        if (defaultColumn) {
          switch (column.pin) {
            case 'left':
              left.push(column);
              break;
            case 'right':
              right.push(column);
              break;
            default:
              center.push(column);
              break;
          }
        } else {
          Logging.Info('Table Column Removed:', column);
          columnsChanged = true;
        }
      });

      // Loop through the Default Columns.  Check for any new Columns
      defaultColumns.forEach((column: VtxColumnProps<T>) => {
        // Find the Column in the Local Storage
        const tableColumn: TableColumn = tableSettings.columns.filter((x: TableColumn) => x.dataIndex === column.dataIndex)[0];

        // New column was added to the Default Columns
        if (!tableColumn) {
          let newColumn: TableColumn = {
            name: column.title.toString(),
            dataIndex: column.dataIndex.toString(),
            visibility: true,
            canChangeVisibilty: true,
            pin: column.fixed?.toString(),
            canChangePin: true,
            canMove: true,
          };

          // Check if the Column was configured in the Additional Table Settings
          const additionalTableColumn: TableColumn = additionalTableSettings
            ? additionalTableSettings.columns.filter((x: TableColumn) => x.dataIndex === column.dataIndex)[0]
            : null;

          // Merge in the Additional Table Settings if Supplied
          if (additionalTableColumn) {
            newColumn = { ...newColumn, ...additionalTableColumn };
          }

          Logging.Info('Table Column Added:', newColumn);
          columnsChanged = true;

          switch (column.fixed) {
            case 'left':
              left.push(newColumn);
              break;
            case 'right':
              right.push(newColumn);
              break;
            default:
              center.push(newColumn);
              break;
          }
          // Default Column was renamed.  Update the Table Column Name
        } else if (tableColumn.name !== column.title.toString()) {
          Logging.Info('Table Column Renamed:', column.dataIndex, column.title, tableColumn.name);
          tableColumn.name = column.title.toString();

          columnsChanged = true;
        }
      });

      // Columns were Added or Removed
      if (columnsChanged) {
        tableSettings = {
          version: tableSettings.version,
          tableKey,
          saveFiltersSorter: tableSettings.saveFiltersSorter,
          showBreadcrumbs: tableSettings.showBreadcrumbs,
          hideDisabledColumns: tableSettings.hideDisabledColumns,
          columns: [],
        };

        tableSettings.columns = tableSettings.columns.concat(left);
        tableSettings.columns = tableSettings.columns.concat(center);
        tableSettings.columns = tableSettings.columns.concat(right);

        const settings: string = JSON.stringify(tableSettings);

        Logging.Info('Update Table Settings:', tableSettings);

        // Update the Local Storage
        localStorage[tableKey] = settings;
      }
    }

    return tableSettings;
  };

  // Default Table Settings
  const [defaultTableSettings] = React.useState(getDefaultTableSettings());

  // User Table Settimngs
  const [tableSettings, setTableSettings] = React.useState(getVerifiedTableSettings(getDefaultColumns()));

  // #endregion

  // #region Get Row Data

  // Get new row data based on the Model
  const getRowData = (
    pageNumber: number,
    pageSize: number,
    filters: GridFilter[],
    sorters: GridSort[],
    bypassCache = false,
  ): void => {
    Logging.Debug('getRowData', model, pageNumber, pageSize, filters, sorters);

    // Prevent duplicate API Requests
    if (!state.loading) {
      if (model === 'server') {
        dispatch(getGridDataServerModel(reducerContext, pageNumber, pageSize, filters, sorters, bypassCache));
      } else if (model === 'client') {
        dispatch(getGridDataClientModel(reducerContext, pageNumber, pageSize, filters, sorters));
      } else if (model === 'static') {
        dispatch(getGridDataStaticModel(reducerContext, pageNumber, pageSize, dataRows, filters, sorters));
      }
    }
  };

  // #endregion

  // #region Row Selection

  // Row Selection has Changed
  const onSelectChange = (rowKeysSelected: React.Key[], rowsSelected: T[]): void => {
    Logging.Debug('On Select:', rowKeysSelected, rowsSelected);

    setSelectedRowKeys(rowKeysSelected);
    setSelectedRows(rowsSelected);

    // Inform the Parent if they are interested in knowing
    if (onSelectedRowsChange) {
      onSelectedRowsChange(rowKeysSelected, rowsSelected);
    }
  };

  // Clear the Row Selection
  const clearSelection = (): void => {
    Logging.Debug('Clearing Row Selection');

    setSelectedRowKeys(null);
    setSelectedRows(null);
  };

  // #endregion

  // #region Window Size

  // Get the Vertical Scroll Height of the Table
  const getVerticalScroll = (height: number): number => {
    const rowHeight = 33;
    const horizontalScrollerHeight = 17;

    // Check if Breadcrumb Filters are present.  Some Filters does not show up in the BreadCrumbs
    const numberBreadCrumbFilters = state.filters && alwaysHiddenFilterBreadcrumbs && alwaysHiddenFilterBreadcrumbs.length
      ? state.filters.filter((x: GridFilter) => !alwaysHiddenFilterBreadcrumbs.includes(x.column)).length
      : state.filters?.length || 0;

    // Check if Breadcrumb Sorters are present.  Some Sorters does not show up in the BreadCrumbs
    const numberBreadCrumbSorters = state.filters && alwaysHiddenSorterBreadcrumbs && alwaysHiddenSorterBreadcrumbs.length
      ? state.sorters.filter((x: GridSort) => !alwaysHiddenSorterBreadcrumbs.includes(x.column)).length
      : state.sorters?.length || 0;

    // Estimate 7 Breadcrumbs per line
    const numberBreadCrumbLines = numberBreadCrumbFilters + numberBreadCrumbSorters
      ? Math.floor((numberBreadCrumbFilters + numberBreadCrumbSorters) / 7) + 1
      : 0;

    // Calculate the Breadcrumbs Height
    const breadCrumbsHeight = ((tableSettings || defaultTableSettings)?.showBreadcrumbs !== ShowBreadCrumbsEnum.NEVER) && numberBreadCrumbLines
      ? (27 * numberBreadCrumbLines) + 2
      : 0;

    const headerManagementHeight = showHeader ? 42 : 0;

    const loadingIndicatorHeight = showLoadingIndicator ? 3 : 0;

    // Calculate the Vertical Height
    const verticalHeight = verticalHeightOffset
      ? height - (verticalHeightOffset + headerManagementHeight + breadCrumbsHeight + loadingIndicatorHeight)
      : undefined;

    // Grid has Rows
    if (verticalHeightOffset && state?.dataRows?.length) {
      const numberRowsWithoutScroll: number = (verticalHeight - horizontalScrollerHeight) / rowHeight;

      Logging.Debug('Vertical Scroll:', verticalHeight, state.dataRows.length, numberRowsWithoutScroll);

      // Only enable Vertical Scroller if there are more Rows than space available
      return state.dataRows.length > numberRowsWithoutScroll ? verticalHeight : undefined;
    }

    // No Data Rows.  Disable the Vertical Scroller
    return undefined;
  };

  // Get the Window Size.  Will constantly be updated it the Window Resizes
  const size = useWindowSize();

  // #endregion

  // #region Table Management

  // Show Table Managment Dialog
  const onTableManagement = (): void => {
    setShowTableManagementDialog(true);
  };

  // Table Management OK Button
  const onTableManagementOK = (settingsHash: string): void => {
    setShowTableManagementDialog(false);

    setTableSettingsHash(settingsHash);
  };

  // Table Management Cancel Button
  const onTableManagementCancel = (): void => {
    setShowTableManagementDialog(false);
  };

  // #endregion

  // #region Columns

  // Get the Hidden Columns
  const getHiddenColumns = (): string[] => {
    if (tableSettings) {
      return tableSettings.columns.filter((column: TableColumn) => column.visibility === false).map((column: TableColumn) => column.dataIndex);
    }

    return [];
  };

  // Add the Column's filteredValue and sortOrder.
  // If Filter Value is specified the Grid will not automatically handle it anymore.  Controlled Mode
  const addColumnsFiltersSorters = (visibleColumns: VtxColumnProps<T>[]): VtxColumnProps<T>[] => {
    visibleColumns.forEach((column: VtxColumnProps<T>) => {
      // Get the Filter for the Column
      const gridFilter: GridFilter = state.filters?.filter((filter: GridFilter) => filter.column === column.dataIndex)[0];

      // Get the Sorter for the Column
      const gridSorter: GridSort = state.sorters?.filter((sorter: GridSort) => sorter.column === column.dataIndex)[0];

      // eslint-disable-next-line no-param-reassign
      column.key = column.dataIndex.toString();

      // eslint-disable-next-line no-param-reassign
      column.filteredValue = gridFilter ? [gridFilter.value] : null;

      // eslint-disable-next-line no-param-reassign
      column.sortOrder = gridSorter ? (gridSorter.order === 'asc' ? 'ascend' : 'descend') : null;
    });

    return visibleColumns;
  };

  // Generate the Columns for the Grid
  const generateColumns = (settings: TableSettings, tableColumns: ShowTableColumnsEnum): VtxColumnProps<T>[] => {
    // Get the Default Columns - Clone the Default Columns as it may be modified
    const defaultColumns: VtxColumnProps<T>[] = _.cloneDeep(getDefaultColumns());

    // Set the Columns according to the User Columns
    if (settings) {
      const visibleTableColumns: TableColumn[] = (tableColumns === ShowTableColumnsEnum.ALL)
        ? settings.columns
        : settings.columns.filter((column: TableColumn) => column.visibility === true);

      const userColumns: VtxColumnProps<T>[] = [];

      // Loop through the Visible Columns
      visibleTableColumns.forEach((column: TableColumn) => {
        // Find the Column
        const userColumn: VtxColumnProps<T> = defaultColumns.filter((x: VtxColumnProps<T>) => x.dataIndex === column.dataIndex)[0];

        // Column found - The Column may have been removed, but still configured in Local Storage
        if (userColumn) {
          userColumn.fixed = column.pin as any; // eslint-disable-line @typescript-eslint/no-explicit-any
          userColumns.push(userColumn);
        }
      });

      Logging.Debug('Setting User Columns', userColumns, settings);

      return addColumnsFiltersSorters(userColumns);
    }

    Logging.Debug('Setting Default Columns', defaultColumns);

    return addColumnsFiltersSorters(defaultColumns);
  };

  // Toggle the Table Columns between All and Favorite
  const onTableColumns = (): void => {
    // Toggle the Show Columns
    const tableColumns = showTableColumns === ShowTableColumnsEnum.ALL ? ShowTableColumnsEnum.FAVORITE : ShowTableColumnsEnum.ALL;

    setShowTableColumns(tableColumns);

    // Re Generate the Grid Columns
    setColumns(generateColumns(tableSettings, tableColumns));
  };

  // Check for Hidden columns
  const hasHiddenColumns = getHiddenColumns().length > 0;

  // #endregion

  // #region Pagination, Filters & Sorters

  // Clear all the Filters
  const onClearFilters = (): void => {
    // Update the Filters
    dispatch(setFiltersSorters(reducerContext, null, state.sorters));

    // Inform the Parent Container if they are interested to know that Filters were cleared
    // Used to sync External Filter (e.g Status)
    if (onFilterSortingChange) {
      onFilterSortingChange(null, state.sorters);
    }

    // Clear the Selection of any Rows
    clearSelection();
  };

  // Clear Sorters
  const onClearSorters = (): void => {
    // Update the Sorters
    dispatch(setFiltersSorters(reducerContext, state.filters, null));

    // Inform the Parent Container if they are interested to know that Sorters were cleared
    if (onFilterSortingChange) {
      onFilterSortingChange(state.filters, null);
    }
  };

  // Delete the Filter from the Breadcrumb
  const onDeleteFilter = (gridFilter: GridFilter): void => {
    Logging.Info('Delete Filter', gridFilter);
    dispatch(deleteFilter(reducerContext, gridFilter));
  };

  // Delete the Sorter from the Breadcrumb
  const onDeleteSorter = (gridSort: GridSort): void => {
    onClearSorters();
  };

  // Load the Filters and Sorters from the Browser Storage
  const loadFiltersAndSorters = (): void => {
    const settings: TableSettings = tableSettings || defaultTableSettings;

    // Get Filters and Sorters from Session Storage
    if (settings.saveFiltersSorter === SaveFiltersSorterEnum.SESSION_STORAGE) {
      const filtersSortersPaging: GridFiltersSortersPaging = sessionStorage[filtersSortersPagingKey]
        ? JSON.parse(sessionStorage[filtersSortersPagingKey]) : null;

      if (filtersSortersPaging) {
        Logging.Debug('Loading from Session Storage:', filtersSortersPaging);
        dispatch(setPaginationFiltersSorters(reducerContext, state.pageNumber, filtersSortersPaging.pageSize || initialPageSize,
          filtersSortersPaging.filters, filtersSortersPaging.sorters));
        return;
      }
    // Get Filters and Sorters from Local Storage
    } else if (settings.saveFiltersSorter === SaveFiltersSorterEnum.LOCAL_STORAGE) {
      const filtersSortersPaging: GridFiltersSortersPaging = localStorage[filtersSortersPagingKey]
        ? JSON.parse(localStorage[filtersSortersPagingKey]) : null;

      if (filtersSortersPaging) {
        Logging.Debug('Loading from Local Storage:', filtersSortersPaging);
        dispatch(setPaginationFiltersSorters(reducerContext, state.pageNumber, filtersSortersPaging.pageSize || initialPageSize,
          filtersSortersPaging.filters, filtersSortersPaging.sorters));
        return;
      }
    }

    // Filters and Sorters Ready
    dispatch(setFiltersSortersReady(reducerContext));
  };

  // Save the Filters and Sorters to the Browser Storage
  const saveFiltersAndSorters = (): void => {
    const settings: TableSettings = tableSettings || defaultTableSettings;

    const filtersSortersPaging: GridFiltersSortersPaging = {
      version: 'v1',
      filters: state.filters,
      sorters: state.sorters,
      pageSize: state.pageSize,
      pageNumber: state.pageNumber,
    };

    // Set Filters and Sorters to Session Storage
    if (settings.saveFiltersSorter === SaveFiltersSorterEnum.SESSION_STORAGE) {
      sessionStorage[filtersSortersPagingKey] = JSON.stringify(filtersSortersPaging);
      Logging.Debug('Saving to Session Storage:', filtersSortersPaging);
    // // Set Filters and Sorters to Local Storage
    } else if (settings.saveFiltersSorter === SaveFiltersSorterEnum.LOCAL_STORAGE) {
      localStorage[filtersSortersPagingKey] = JSON.stringify(filtersSortersPaging);
      Logging.Debug('Saving to Local Storage:', filtersSortersPaging);
    } else {
      // Clear both Local and Session Storage
      sessionStorage[filtersSortersPagingKey] = null;
      localStorage[filtersSortersPagingKey] = null;
    }
  };

  // Check if the External Filter's value is merged into the State Filter
  const isExternalFilterMerged = (): boolean => {
    let isMerged = true;

    // External Filter is defined
    if (externalFilter) {
      // Find the corresponding State Filter
      const stateFilter: GridFilter = state.filters?.filter((filter: GridFilter) => filter.column === externalFilter.column)[0];

      // External Filter is Added / Changed
      if (externalFilter.value) {
        // Check if State Filter and Extermal Filter has the same value
        isMerged = (stateFilter !== undefined) && (stateFilter?.value === externalFilter.value);
      // External Filter is Deleted
      } else {
        // Check if the State Filter is Removed
        isMerged = !stateFilter;
      }

      Logging.Debug('isExternalFilterMerged:', isMerged, externalFilter, stateFilter);
    }

    return isMerged;
  };

  // Pagination, Filters or Sorters has Changed
  const handlePaginationFiltersSorters = (
    paginationConfig: VtxPaginationConfig,
    filters: Record<string, React.ReactText[]>,
    sorter: SorterResult | SorterResult[],
  ) => {
    Logging.Debug('handlePaginationFiltersSorters', paginationConfig, filters, sorter);

    const gridFilters: GridFilter[] = [];

    // Convert the Filters to Grid Filters []
    if (filters) {
      Object.keys(filters).forEach((filter: string) => {
        // Filter has a Value
        if (filters[filter]) {
          gridFilters.push({
            column: filter,
            value: filters[filter][0] as string,
          });
        }
      });
    }

    const gridSorters: GridSort[] = [];

    // Convert the Sorters to Grid Sorters []
    if (sorter) {
      if (Array.isArray(sorter)) {
        sorter.forEach((sort: SorterResult) => {
          if (sort.field && sort.order) {
            gridSorters.push({
              column: sort.field.toString(),
              order: sort.order === 'ascend' ? 'asc' : 'desc',
            });
          }
        });
      } else {
        // eslint-disable-next-line no-lonely-if
        if (sorter.field && sorter.order) {
          gridSorters.push({
            column: sorter.field.toString(),
            order: sorter.order === 'ascend' ? 'asc' : 'desc',
          });
        }
      }
    }

    // The Filters & Sorters for any Hidden Columns will not set.
    // Look at the current Filters & Sorters State and set them back
    if (showTableColumns === ShowTableColumnsEnum.FAVORITE) {
      // Get all the Hidden Columns
      const hiddenColumns: string[] = getHiddenColumns();

      Logging.Debug('Hidden Columns:', hiddenColumns);

      // Loop through the Hidden columns
      hiddenColumns.forEach((column: string) => {
        // Check if a State Filter is configured for the Hidden Column
        const stateFilter: GridFilter = state.filters?.filter((filter: GridFilter) => filter.column === column)[0];

        // Filter was found
        if (stateFilter) {
          const alreadyExist = gridFilters?.filter((filter: GridFilter) => filter.column === stateFilter.column)[0];

          if (alreadyExist === undefined) {
            Logging.Info('Add Hidden Filter:', stateFilter);

            gridFilters.push(stateFilter);
          }
        }

        // There can only be one Grid Sorter
        if (gridSorters.length === 0) {
          // Check if a State Sorter is configured for the Hidden Column
          const stateSorter: GridSort = state.sorters?.filter((sort: GridSort) => sort.column === column)[0];

          // Sorter was found
          if (stateSorter) {
            Logging.Info('Add Hidden Sorter:', stateSorter);
            gridSorters.push(stateSorter);
          }
        }
      });
    }

    // Save the Pagination, Filters and Sorters to the State
    dispatch(setPaginationFiltersSorters(reducerContext, paginationConfig.current, paginationConfig.pageSize,
      gridFilters.length ? gridFilters : null, gridSorters.length ? gridSorters : null));

    // Inform the Parent Container if they are interested to know that Filters, Sorting or Pagination has Changed
    // used to update the External Filter, e.g Status
    if (onFilterSortingChange) {
      onFilterSortingChange(gridFilters, gridSorters);
    }

    // Clear the Selection of any Rows
    clearSelection();
  };

  // Set the Current Pagination
  const pagination: VtxPaginationConfig = {
    current: state.pageNumber,
    pageSize: state.pageSize,
    total: state.totalNumberRows,
    pageSizeOptions,
    hideOnSinglePage: hidePagingOnSinglePage,
    showLessItems: true,
    showQuickJumper: true,
    showSizeChanger: true,
    showTitle: false,
    responsive: true,
    position: 'bottom',
  };

  // #endregion

  // #region Other

  // Clear the Notification Message
  const onNotificationClose = (): void => {
    dispatch(clearNotification(reducerContext));
  };

  // Validate the Configuration for any Errors
  const validateConfiguration = (): boolean => {
    let errorTitle: string = null;
    let errorDescription: string = null;

    if (model === 'server') {
      if (apiGetRowsServerModel === null) {
        errorTitle = 'Configuration Error: \'apiGetRowsServerModel\'';
        errorDescription = '\'apiGetRowsServerModel\' method must be specified if Server model is used.';
      }
    } else if (model === 'client') {
      if (apiGetRowsClientModel === null) {
        errorTitle = 'Configuration Error: \'apiGetRowsClientModel\'';
        errorDescription = '\'apiGetRowsClientModel\' method must be specified if Client model is used.';
      }
    } else if (model === 'static') {
      if (apiGetRowsClientModel === null) {
        errorTitle = 'Configuration Error: \'dataRows\'';
        errorDescription = '\'dataRows\' property must be specified if Static model is used.';
      }
    }

    const defaultColumns: VtxColumnProps<T>[] = getDefaultColumns();

    defaultColumns.forEach((column: VtxColumnProps<T>) => {
      if (model === 'server') {
        // For Server Model the Sorting is handled by the Server
        if (column.sorter && (typeof column.sorter !== 'boolean')) {
          errorTitle = `Configuration Error: '${column.title}' Column`;
          errorDescription = `'${column.title}' column is configured for Client sorting while Server model is used. See 'sorter'.`;
        }

        // For Server Model the Filtering is handled by the Server
        if (column.sorter && column.onFilter) {
          errorTitle = `Configuration Error: '${column.title}' Column`;
          errorDescription = `'${column.title}' column is configured for Client filtering while Server model is used. See 'onFilter'.`;
        }
      } else if (model === 'client') {
        // For Client Model the Sorting is handled by the Client
        if (column.sorter && (typeof column.sorter === 'boolean')) {
          errorTitle = `Configuration Error: '${column.title}' Column`;
          errorDescription = `'${column.title}' column is configured for Server sorting while Client model is used. See 'sorter'.`;
        }
      } else if (model === 'static') {
        // For Static Model the Sorting is handled by the Client
        if (column.sorter && (typeof column.sorter === 'boolean')) {
          errorTitle = `Configuration Error: '${column.title}' Column`;
          errorDescription = `'${column.title}' column is configured for Server sorting while Static model is used. See 'sorter'.`;
        }
      }
    });

    if (errorTitle && errorDescription) {
      const notification: NotificationData = {
        type: 'Warning',
        autoClose: false,
        title: errorTitle,
        description: errorDescription,
      };

      Logging.Debug('Generic Grid Configuration Error', notification);

      dispatch(setNotification(reducerContext, notification));

      return false;
    }

    return true;
  };

  // #endregion

  // #region React.useEffect

  // Mount / Unmount
  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Info('Generic Grid Mount:', id);

    // Load Filters and Sorters from Storage if Needed
    loadFiltersAndSorters();

    return (): void => {
      // This Code will run once the component unmounts.
      Logging.Info('Generic Grid Unmount:', id);

      dispatch(clearNotification(reducerContext));
    };
  }, []);

  // External Filter has Changed.  Update the Internal Filters
  React.useEffect(() => {
    if (externalFilter) {
      Logging.Debug('External Filter:', externalFilter);

      // External filter has a Value
      if (externalFilter.value) {
        Logging.Debug('Add External Filter:', externalFilter);
        dispatch(addFilter(reducerContext, externalFilter));
      } else {
        Logging.Debug('Delete External Filter:', externalFilter);
        dispatch(deleteFilter(reducerContext, externalFilter));
      }

      // Clear the Selection of any Rows
      clearSelection();
    }
  }, [externalFilter]);

  // Static Data Rows Changed - Update the Data Rows in the State
  // Used by the Static Model
  React.useEffect(() => {
    if (dataRows) {
      // Recreate the Columns, Set the Column Filters and Sorters
      // This prevents Stale State Data that may be used in Column Callbacks
      setColumns(generateColumns(tableSettings, showTableColumns));

      getRowData(state.pageNumber, state.pageSize, null, null);
    }
  }, [dataRows]);

  // Table Settings has Changed
  React.useEffect(() => {
    if (tableSettingsHash !== 'INITIAL') {
      Logging.Info('Regenerating Columns', tableSettingsHash);

      // Update the Table Settings
      const updatedTableSettings: TableSettings = getVerifiedTableSettings(getDefaultColumns());
      setTableSettings(updatedTableSettings);

      // Re Generate the Grid Columns
      setColumns(generateColumns(updatedTableSettings, showTableColumns));
    }
  }, [tableSettingsHash]);

  // Auto Refresh the Data
  const onAutoRefresh = (): void => {
    Logging.Info('Auto Refresh');
    getRowData(state.pageNumber, state.pageSize, state.filters, state.sorters);
  };

  // Manual Refresh Key changed.  Reload the Data
  React.useEffect(() => {
    // Manual Refresh
    if (refreshKey) {
      Logging.Info('Manual Refresh:', refreshKey);

      // Server Model Only or First Load
      if (model === 'server') {
        // Validate the Configuration.  If successfull reload the Data
        if (validateConfiguration()) {
          // Refresh the new Rows - Bypass the Cache
          getRowData(state.pageNumber, state.pageSize, state.filters, state.sorters, true);
        }
      }
    }
  }, [refreshKey]);

  // Pagination, Filters & Sorters Changed
  React.useEffect(() => {
    // Wait until the External Filter is Merged into the State.  Reduce unnessary Data Loads until State Filters are set
    if (isExternalFilterMerged() && state.filtersSortersReady) {
      Logging.Info('Pagination, Filters & Sorters:', state.pageNumber, state.pageSize, state.filters, state.sorters);

      // Save the Filters and Sorters if needed
      saveFiltersAndSorters();

      // Recreate the Columns, Set the Column Filters and Sorters
      setColumns(generateColumns(tableSettings, showTableColumns));

      // Server Model Only or First Load
      if ((model === 'server') || (!state.dataRows)) {
        // Validate the Configuration.  If successfull load the initial Data
        if (validateConfiguration()) {
          // Refresh the new Rows
          getRowData(state.pageNumber, state.pageSize, state.filters, state.sorters);
        }
      }
    } else {
      Logging.Info('Waiting for Filters & Sorters ...');
    }
  }, [state.filtersSortersReady, state.pageNumber, state.pageSize, state.filters, state.sorters]);

  // Recreate the Auto Refesh to prevent Stale Closure in setInterval
  React.useEffect(() => {
    let interval: NodeJS.Timeout = null;

    // Create the Auto Refresh Timer if needed
    if (autoRefreshSeconds) {
      if (useCache) {
        Logging.Warning('Using Cache and AutoRefresh should not happen simultanously');
      }

      Logging.Debug('Creating AutoRefresh');
      interval = setInterval(onAutoRefresh, autoRefreshSeconds * 1000);
    }

    return (): void => {
      clearInterval(interval);
    };
  }, [state.pageNumber, state.pageSize, state.filters, state.sorters]);

  // #endregion

  return (
    <>
      {
        (showLoadingMessage && (state.dataRows === null))
          ? (
            <ContentLoading loadingMessage={`Loading ${dataTypeDescription} ...`} />
          )
          : (
            <>
              {
                showHeader
                && (
                  <div className="genericGridHeader">
                    <div className="genericGridHeaderLeft">
                      {
                        bulkActions?.actions.length && (
                          <BulkActions
                            title={bulkActions.title || 'Bulk Actions'}
                            selectedRowKeys={selectedRowKeys}
                            selectedRows={selectedRows}
                            actions={bulkActions.actions}
                            type={bulkActions.type || 'primary'}
                            clearSelection={clearSelection}
                          />
                        )
                      }
                      {
                        showTableManagement
                        && (
                          <div className="genericGridHeaderButton">
                            <VtxTooltip title="Configure the Table Settings">
                              <VtxButton
                                id="tableManagementButton"
                                icon={
                                  <VtxIcon type="setting" fontSize={20} className="vtxIconLarge" />
                                }
                                onClick={onTableManagement}
                                type="link"
                              >
                                Table Settings
                              </VtxButton>
                            </VtxTooltip>
                          </div>
                        )
                      }
                      {
                        showAllFavoriteColumns && hasHiddenColumns
                        && (
                          <div className="genericGridHeaderButton">
                            <VtxTooltip title="Toggle between showing All or Favorite columns">
                              <VtxButton
                                id="tableColumnsButton"
                                icon={
                                  <VtxIcon type={showTableColumns === ShowTableColumnsEnum.ALL ? 'table' : 'star'} fontSize={20} className="vtxIconLarge" /> // eslint-disable-line max-len
                                }
                                onClick={onTableColumns}
                                type="link"
                              >
                                {showTableColumns === ShowTableColumnsEnum.ALL ? 'Showing All Columns' : 'Showing Favorite Columns'}
                              </VtxButton>
                            </VtxTooltip>
                          </div>
                        )
                      }
                      {
                        showClearFilters && state.filters
                        && (
                          <div className="genericGridHeaderButton">
                            <VtxTooltip title={state.filters.length > 1 ? `Clear All Filters (${state.filters.length})` : 'Clear Filter'}>
                              <VtxButton
                                id="clearFiltersButton"
                                icon={
                                  <VtxIcon type="delete" fontSize={20} className="vtxIconLarge" />
                                }
                                onClick={onClearFilters}
                                type="link"
                              >
                                {state.filters.length > 1 ? `Clear All Filters (${state.filters.length})` : 'Clear Filter'}
                              </VtxButton>
                            </VtxTooltip>
                          </div>
                        )
                      }
                      {
                        showClearSorters && state.sorters
                        && (
                          <div className="genericGridHeaderButton">
                            <VtxTooltip title="Clear Sorting">
                              <VtxButton
                                id="clearSortersButton"
                                icon={
                                  <VtxIcon type="delete" fontSize={20} className="vtxIconLarge" />
                                }
                                onClick={onClearSorters}
                                type="link"
                              >
                                Clear Sorting
                              </VtxButton>
                            </VtxTooltip>
                          </div>
                        )
                      }
                    </div>
                    <div className="genericGridHeaderRight" />
                  </div>
                )
              }
              {
                (tableSettings || defaultTableSettings)?.showBreadcrumbs !== ShowBreadCrumbsEnum.NEVER
                && (
                  <Breadcrumbs
                    filters={state.filters}
                    sorters={state.sorters}
                    defaultTableSettings={defaultTableSettings}
                    tableSettings={tableSettings}
                    showTableColumns={showTableColumns}
                    alwaysHiddenFilterBreadcrumbs={alwaysHiddenFilterBreadcrumbs}
                    alwaysHiddenSorterBreadcrumbs={alwaysHiddenSorterBreadcrumbs}
                    onDeleteFilter={onDeleteFilter}
                    onDeleteSorter={onDeleteSorter}
                    showBreadcrumbs={(tableSettings || defaultTableSettings)?.showBreadcrumbs}
                  />
                )
              }
              {
                state.loading && showLoadingIndicator
                  ? (
                    <div id="loadingIndicator" className="progressLine" />
                  )
                  : (
                    <div className="progressLineHidden" />
                  )
              }
              <div className="genericGrid">
                <VtxTable<T>
                  rowKey={getRowKey ? (row: T) => getRowKey(row) : undefined}
                  columns={columns}
                  dataSource={state.dataRows}
                  pagination={pagination || false}
                  expandRowByClick={expandRowByClick}
                  onChange={handlePaginationFiltersSorters}
                  scroll={{
                    x: horizontalWidth === 'max-content' ? 'max-content' : undefined,
                    y: getVerticalScroll(size.height),
                  }}
                  rowSelection={bulkActions ? {
                    columnWidth: 40,
                    type: (selectionType === 'multiple' ? 'checkbox' : 'radio'),
                    onChange: (rowKeysSelected: React.Key[], rowsSelected: T[]) => onSelectChange(rowKeysSelected, rowsSelected),
                    selectedRowKeys,
                  } : undefined}
                  expandable={{
                    expandedRowRender: expandedRowRender ? (row: T) => expandedRowRender(row) : null,
                    rowExpandable: rowExpandable ? (row: T) => rowExpandable(row) : null,
                    // eslint-disable-next-line no-confusing-arrow
                    expandIcon: (!expanderIcons) ? undefined : ({ expanded, onExpand, record }) => expanded
                      ? (
                        <div className="expanderIcon">
                          <VtxButton
                            id="collapseButton"
                            type="link"
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onClick={(e: any) => onExpand(record, e)}
                            icon={<VtxIcon type={expanderIcons.collapseIcon} fontSize={10} />}
                          />
                        </div>
                      )
                      : (
                        <div className="expanderIcon">
                          <VtxButton
                            id="expandButton"
                            type="link"
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onClick={(e: any) => onExpand(record, e)}
                            icon={<VtxIcon type={expanderIcons.expandIcon} fontSize={10} />}
                          />
                        </div>
                      ),
                  }}
                  loading={state.dataRows === null}
                />
              </div>
            </>
          )
      }
      <Notification
        data={state.notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
      {
        (showTableManagementDialog && state.dataRows) && (
          <TableManagement
            isOpen={showTableManagementDialog}
            onOK={onTableManagementOK}
            onCancel={onTableManagementCancel}
            tableKey={tableKey}
            defaultTableSettings={defaultTableSettings}
            leftPinnedColumnsVisible
          />
        )
      }
    </>
  );
};

export default GenericGrid;
